<template>
<form autocomplete="off">
    <div class="row">
        <div class="col-12 col-lg-6">
            <base-input
                v-if="!countryFieldFocus"
                :name="$t('form.address.country')"
                type="text"
                :placeholder="$t('form.address.country')"
                v-model="model.location.country"
                @focus="countryNameFocus"
            />
            <auto-complete-input
                v-else
                class="country-auto-complete"
                :name="$t('form.address.country')"
                :label="$t('form.address.country')"
                :values="staticCountries"
                idkey="code"
                namekey="name"
                v-model="country"
                return-object
            />
        </div>
        <base-input
            class="col-12 col-lg-6"
            :name="$t('form.address.cep')"
            type="text"
            :placeholder="$t('form.address.cep')"
            v-model="model.location.zip"
            @input="zipCodeFormat"
            prepend-icon="mdi mdi-magnify"
            v-validate="'required'"
            :error="errors.first($t('form.address.cep'))"
            @blur="getLocation($event.target.value)"
            @focus="clearZipError"
        />
    </div>
    <div class="row">
        <base-input
            class="col-12 col-lg-4"
            :name="$t('form.address.address')"
            type="text"
            v-validate="'required'"
            :disabled="disableFields"
            :placeholder="$t('form.address.address')"
            v-model="model.location.address"
            :error="errors.first($t('form.address.address'))"
        />
        <base-input
            class="col-12 col-lg-4"
            :name="$t('form.address.number')"
            type="text"
            :disabled="disableFields"
            :placeholder="$t('form.address.number')"
            v-validate="'required'"
            :error="errors.first($t('form.address.number'))"
            v-model="model.location.number"
        />
        <base-input
            class="col-12 col-lg-4"
            :name="$t('form.address.neighborhood')"
            type="text"
            v-validate="'required'"
            :disabled="disableFields"
            :placeholder="$t('form.address.neighborhood')"
            :error="errors.first($t('form.address.neighborhood'))"
            v-model="model.location.neighborhood"
        />
    </div>
    <div class="row">
        <base-input
            class="col-12"
            name="info"
            type="text"
            :disabled="disableFields"
            :placeholder="$t('form.address.info')"
            v-model="model.location.info"
        />
    </div>
    <div class="row">
        <div class="col-12 col-lg-6">
            <select
                v-if="foundZip"
                class="form-control"
                name="city"
                :disabled="disableFields"
                v-model="model.location.city"
            >
                <option value disabled selected>{{ $t('form.address.city') }}</option>
                <option
                    :value="model.location.city"
                >{{ model.location.city || $t('form.address.fill_zip') }}</option>
            </select>
            <base-input
                v-else
                :name="$t('form.address.city')"
                type="text"
                :disabled="disableFields"
                v-model="model.location.city"
                :placeholder="$t('form.address.city')"
                v-validate="'required'"
                :error="errors.first($t('form.address.city'))"
            />
        </div>
        <div class="col-12 col-lg-6">
            <base-input
                v-if="this.country.name !== 'Brasil' && this.country.name !== 'Brazil'"
                :name="$t('form.address.city')"
                type="text"
                :disabled="disableFields"
                v-model="model.location.state"
                :placeholder="$t('form.address.state')"
                v-validate="'required'"
                :error="errors.first($t('form.address.state'))"
            />
            <select
                v-else
                class="form-control"
                :name="$t('form.address.state')"
                type="text"
                :disabled="disableFields"
                v-model="model.location.state"
                :placeholder="$t('form.address.state')"
                v-validate="`required|regex:^[A-Z]{2}$|included:${validStates.toString()}`"
                :error="errors.first($t('form.address.state'))"
            >
                <option value disabled selected>{{$t('form.address.state')}}</option>
                <option
                    v-for="state in validStates"
                    :key="state"
                    :value="state"
                >
                    {{state}}
                </option>
            </select>
        </div>
    </div>
    <base-input
        v-if="profile"
        :name="'name'"
        type="text"
        v-validate="'required'"
        :placeholder="'Tipo de endereço'"
        v-model="model.name"
        :error="errors.first('name')"
    />
</form>
</template>

<script>
import { mapGetters } from 'vuex';

import BaseInput from '../../../argon/components/Inputs/BaseInput';
import AutoCompleteInput from '../../Common/AutoCompleteInput';

import { format } from '../../../utils/Mask';

export default {
    emits: ['input', 'update:modelValue'],
    name: 'AddressForm',
    components: {
        BaseInput,
        AutoCompleteInput,
    },

    props: {
        modelValue: Object,
        countryReadonly: Boolean,
        hideNumber: Boolean,
        hideNeighborhood: Boolean,
        noValidate: Boolean,
        hideComplement: Boolean,
        profile: Boolean,
        searchAddressByApi: {
            type: Boolean,
            default: true,
            required: false,
        }
    },

    data() {
        return {
            loadingCountries: false,
            loadingStates: false,
            loadingCities: false,
            mounting: true,
            foundZip: false,
            disableFields: false,
            country: {
                name: this.modelValue.location.country ?? '',
                code: this.modelValue.location.code ?? '',
            },
            countryFieldFocus: false,
        };
    },

    mounted() {
        if (this.countries.length === 0) {
            this.getCountries();
        }
        if (this.model && this.model.code) {
            this.$store.commit('PUSH_STATE', this.model.state);
            this.$store.commit('PUSH_CITY', this.model.city);
            this.location = this.model;
        } else {
            this.location = {};
        }
        this.setCountries(this.country);
    },
    watch: {
        country: {
            deep: true,
            handler(val) {
                this.setCountries(val);
            },
        },
        location(value) {
            if (this.profile) {
                this.model = {
                    name: '',
                    location: value,
                };
            } else {
                this.model = {
                    location: { ...value },
                };
            }
        },
        validating(val) {
            if (val) {
                this.$validator.validate().then(async (valid) => {
                    await this.$store.commit('IS_VALID', valid);
                });
            }
        },
    },
    methods: {
        countryNameFocus() {
            this.countryFieldFocus = true;
        },
        setCountries(val) {
            this.model.location = {
                ...this.model.location,
                country: val.name,
                code: val.code,
            };
        },
        getCountries() {
            this.$loading.show();
            this.$store
                .dispatch('INDEX_COUNTRIES')
                .then(() => this.$loading.close());
        },

        getLocation(zip) {
            // Se for Brasil, busca o CEP por meio da api
            if (this.country.code === 'BR' && this.searchAddressByApi) {
                this.$loading.show();
                this.$store
                    .dispatch('GET_LOCATION_BY_ZIP', {
                        zip: zip,
                        country: this.country.name,
                    })
                    .finally(() => this.$loading.close());
            }
        },
        clearZipError() {
            this.$store.commit('CLEAR_ZIP_CODE');
        },
        // Vue3 expõe computed como getter, não função — usado como
        // @input="zipCodeFormat" isso lançava TypeError e derrubava a página.
        zipCodeFormat() {
            const zip = this.model.location.zip || '';
            if (this.model.location.code && (this.model.location.code === 'BR')) {
                let formatedZipCode = zip.replace(/\D/g, '');

                if (zip.length >= 5 && zip.length < 9) {
                    formatedZipCode = zip.substring(0, 5) + '-' + zip.substring(5, 8);
                }

                this.model.location.zip = formatedZipCode;
            }
        },
    },
    computed: {
        ...mapGetters({
            language: 'LANGUAGE',
            countries: 'LOADED_COUNTRIES',
            states: 'LOADED_STATES',
            cities: 'LOADED_CITIES',
            countryIsSelected: 'SELECTED_COUNTRY_CODE',
            location: 'LOADED_LOCATION',
            validating: 'VALIDATING',
            zipStatus: 'ZIP_STATUS',
        }),
        model: {
            get() { return this.modelValue; },
            set(value) { this.$emit('update:modelValue', value); },
        },
        mask() {
            return format(this.model.location.code);
        },
        validStates() {
            return ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
        },
        staticCountries() {
            return this.$store.getters.STATIC_COUNTRIES;
        }
    },
};
</script>

<style scoped>
.country-auto-complete {
    margin-bottom: 1.5rem;
}
</style>
